import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Building, MapPin, Phone, Mail, Globe, Users, AlertCircle } from 'lucide-react';
import onboardingService from '../../../services/onboardingService';
import { useAuth } from '../../../contexts/AuthContext';

const DEFAULT_FORM = {
  name: '',
  dotNumber: '',
  mcNumber: '',
  companyId: '',
  address: {
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States'
  },
  phone: '',
  email: '',
  website: '',
  fleetSize: '',
  subscriptionPlan: 'basic'
};

const STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const SUBSCRIPTION_PLANS = [
  { value: 'basic', label: 'Basic Plan', description: 'Essential features for small fleets' },
  { value: 'pro', label: 'Pro Plan', description: 'Advanced features for growing businesses' },
  { value: 'enterprise', label: 'Enterprise Plan', description: 'Full-featured solution for large operations' }
];

const stableStringify = (value) => {
  if (value === undefined) {
    return 'null';
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  if (value && typeof value === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys.map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  }

  return JSON.stringify(value);
};

const deepEqual = (a, b) => stableStringify(a) === stableStringify(b);

const stripCompanyMeta = (company = {}) => {
  const {
    isPartial,
    lastSaved,
    lastSavedAt,
    readyForSubmission,
    completedAt,
    createdAt,
    updatedAt,
    metadata,
    ...rest
  } = company;
  return rest;
};

const normalizeCompanyData = (raw = {}) => {
  const sanitized = stripCompanyMeta(raw);
  const { address: rawAddress = {}, ...rest } = sanitized;

  const normalized = {
    ...DEFAULT_FORM,
    ...rest,
    address: {
      ...DEFAULT_FORM.address,
      ...rawAddress
    }
  };

  normalized.dotNumber = normalized.dotNumber ? String(normalized.dotNumber) : '';
  normalized.mcNumber = normalized.mcNumber ? String(normalized.mcNumber) : '';
  normalized.name = normalized.name || '';
  normalized.phone = normalized.phone || '';
  normalized.email = normalized.email || '';
  normalized.website = normalized.website || '';
  normalized.fleetSize =
    normalized.fleetSize === undefined || normalized.fleetSize === null || normalized.fleetSize === ''
      ? ''
      : String(normalized.fleetSize);

  // Ensure address strings are never null to avoid diff loops
  normalized.address.street = normalized.address.street || '';
  normalized.address.city = normalized.address.city || '';
  normalized.address.state = normalized.address.state || '';
  normalized.address.zipCode = normalized.address.zipCode || '';
  normalized.address.country = normalized.address.country || 'United States';

  return normalized;
};

const hasCompanyData = (company = {}) => {
  const fieldsToCheck = ['name', 'dotNumber', 'mcNumber', 'phone', 'email', 'website'];
  if (fieldsToCheck.some(field => company[field] && String(company[field]).trim() !== '')) {
    return true;
  }

  if (company.address) {
    const { street, city, state, zipCode } = company.address;
    if ([street, city, state, zipCode].some(value => value && String(value).trim() !== '')) {
      return true;
    }
  }

  return false;
};

const sanitizeCompanyPayload = (form) => {
  const payload = {
    name: form.name?.trim() || '',
    dotNumber: form.dotNumber?.trim() || '',
    mcNumber: form.mcNumber?.trim() || null,
    address: {
      street: form.address?.street?.trim() || '',
      city: form.address?.city?.trim() || '',
      state: form.address?.state?.trim() || '',
      zipCode: form.address?.zipCode?.trim() || '',
      country: form.address?.country || 'United States'
    },
    phone: form.phone?.trim() || null,
    email: form.email?.trim() || null,
    website: form.website?.trim() || null,
    fleetSize: form.fleetSize !== '' && form.fleetSize !== null
      ? Number(form.fleetSize)
      : null,
    subscriptionPlan: form.subscriptionPlan || 'basic'
  };

  if (!payload.mcNumber) {
    payload.mcNumber = null;
  }

  if (Number.isNaN(payload.fleetSize)) {
    payload.fleetSize = null;
  }

  if (!payload.phone) {
    payload.phone = null;
  }

  if (!payload.email) {
    payload.email = null;
  }

  if (!payload.website) {
    payload.website = null;
  }

  return payload;
};

const CompanySetupStep = ({ data, updateData, registerNextHandler }) => {
  const { currentUser } = useAuth();
  const normalizedCompanyFromProps = useMemo(
    () => normalizeCompanyData(data?.company || {}),
    [data?.company]
  );
  const normalizedCompanySignature = useMemo(
    () => stableStringify(normalizedCompanyFromProps),
    [normalizedCompanyFromProps]
  );

  const [formData, setFormData] = useState(normalizedCompanyFromProps);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const lastSavedPayloadRef = useRef(null);
  const lastSyncedFormRef = useRef(null);
  const lastNormalizedSignatureRef = useRef(null);
  const isHydratingRef = useRef(false);
  const formDataSignature = useMemo(() => stableStringify(formData), [formData]);

  useEffect(() => {
    if (lastNormalizedSignatureRef.current === normalizedCompanySignature) {
      return;
    }

    lastNormalizedSignatureRef.current = normalizedCompanySignature;

    if (formDataSignature === normalizedCompanySignature) {
      return;
    }

    isHydratingRef.current = true;
    setFormData(normalizedCompanyFromProps);
  }, [normalizedCompanySignature, normalizedCompanyFromProps, formDataSignature]);

  useEffect(() => {
    if (!currentUser?.uid) {
      return;
    }

    if (hasCompanyData(normalizedCompanyFromProps)) {
      return;
    }

    let isCancelled = false;

    const loadCompanyDraft = async () => {
      try {
        const progress = await onboardingService.getCompanyOnboardingProgress(currentUser.uid);
        let savedCompany = progress?.data?.company;

        if (!hasCompanyData(savedCompany)) {
          const companyDraft = await onboardingService.getCompanyDraft(currentUser.uid);
          if (companyDraft && hasCompanyData(companyDraft)) {
            savedCompany = companyDraft;
          }
        }

        if (savedCompany && hasCompanyData(savedCompany)) {
          const normalized = normalizeCompanyData(savedCompany);
          const signature = stableStringify(normalized);

          if (!isCancelled && lastNormalizedSignatureRef.current !== signature) {
            lastNormalizedSignatureRef.current = signature;
            isHydratingRef.current = true;
            setFormData(normalized);
          }
        }
      } catch (error) {
        console.error('❌ COMPANY-STEP: Failed to load company draft data:', error);
      }
    };

    loadCompanyDraft();

    return () => {
      isCancelled = true;
    };
  }, [currentUser?.uid, normalizedCompanySignature, normalizedCompanyFromProps]);

  useEffect(() => {
    if (isHydratingRef.current) {
      isHydratingRef.current = false;
      return;
    }

    const payload = sanitizeCompanyPayload(formData);
    const enrichedPayload = formData.companyId
      ? { ...payload, companyId: formData.companyId }
      : payload;
    const payloadSignature = stableStringify(enrichedPayload);

    if (payloadSignature === lastSyncedFormRef.current) {
      return;
    }

    lastSyncedFormRef.current = payloadSignature;
    updateData('company', enrichedPayload, { skipRemote: true });
  }, [formData, updateData]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (event) => {
    let value = event.target.value.replace(/\D/g, '');

    if (value.length >= 6) {
      value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
    } else if (value.length >= 3) {
      value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    }

    setFormData(prev => ({
      ...prev,
      phone: value
    }));

    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleZipChange = (event) => {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 5) {
      value = `${value.slice(0, 5)}-${value.slice(5, 9)}`;
    }

    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        zipCode: value
      }
    }));

    if (errors['address.zipCode']) {
      setErrors(prev => ({ ...prev, 'address.zipCode': '' }));
    }
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Company name is required';
    }

    if (!formData.dotNumber.trim()) {
      newErrors.dotNumber = 'DOT number is required';
    } else if (!/^\d{1,8}$/.test(formData.dotNumber.trim())) {
      newErrors.dotNumber = 'DOT number must be 1-8 digits';
    }

    if (!formData.address.street.trim()) {
      newErrors['address.street'] = 'Street address is required';
    }

    if (!formData.address.city.trim()) {
      newErrors['address.city'] = 'City is required';
    }

    if (!formData.address.state.trim()) {
      newErrors['address.state'] = 'State is required';
    }

    if (!formData.address.zipCode.trim()) {
      newErrors['address.zipCode'] = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.address.zipCode.trim())) {
      newErrors['address.zipCode'] = 'ZIP code must be 12345 or 12345-6789';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }

    if (formData.phone && !/^\(\d{3}\) \d{3}-\d{4}$/.test(formData.phone)) {
      newErrors.phone = 'Phone must be in format (555) 123-4567';
    }

    if (formData.fleetSize !== '' && Number(formData.fleetSize) < 0) {
      newErrors.fleetSize = 'Fleet size cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  useEffect(() => {
    if (!registerNextHandler) {
      return undefined;
    }

    const unregister = registerNextHandler(async () => {
      const isValid = validateForm();
      if (!isValid) {
        console.warn('⚠️ COMPANY-STEP: Validation failed, blocking Next progression');
        return false;
      }

      if (!currentUser?.uid) {
        setErrors(prev => ({
          ...prev,
          submit: 'You must be signed in to continue the onboarding process.'
        }));
        return false;
      }

      const payload = sanitizeCompanyPayload(formData);

      try {
        setIsSaving(true);
        console.log('📡 COMPANY-STEP: Saving company information via API before advancing', {
          userId: currentUser.uid,
          payload
        });
        const apiResult = await onboardingService.saveCompanyDataViaApi(currentUser.uid, payload, false);
        const companyIdFromApi = apiResult?.companyId || data?.company?.companyId || null;
        const synchronizedPayload = companyIdFromApi
          ? { ...payload, companyId: companyIdFromApi }
          : { ...payload };

        lastSavedPayloadRef.current = stableStringify(synchronizedPayload);
        setLastSavedAt(new Date());
        await updateData('company', synchronizedPayload, { skipRemote: true });
        
        if (companyIdFromApi && !formData.companyId) {
          setFormData(prev => ({ ...prev, companyId: companyIdFromApi }));
        }
        console.log('✅ COMPANY-STEP: Company information saved successfully via API');
        return true;
      } catch (error) {
        console.error('❌ COMPANY-STEP: Failed to save company information', error);
        setErrors(prev => ({
          ...prev,
          submit: error.message || 'Failed to save company information. Please try again.'
        }));
        return false;
      } finally {
        setIsSaving(false);
      }
    });

    return () => unregister?.();
  }, [registerNextHandler, validateForm, currentUser?.uid, formData]);

  return (
    <form onSubmit={(event) => event.preventDefault()} className="space-y-6">
      {errors.submit && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{errors.submit}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <Building className="h-5 w-5 mr-2 text-blue-600" />
          Company Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Company Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter company name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.name}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="fleetSize" className="block text-sm font-medium text-gray-700">
              Fleet Size
            </label>
            <div className="mt-1 relative">
              <Users className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="number"
                id="fleetSize"
                name="fleetSize"
                min="0"
                value={formData.fleetSize}
                onChange={handleInputChange}
                className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.fleetSize ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Number of vehicles"
              />
            </div>
            {errors.fleetSize && (
              <p className="mt-1 text-sm text-red-600">{errors.fleetSize}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="dotNumber" className="block text-sm font-medium text-gray-700">
              DOT Number *
            </label>
            <input
              type="text"
              id="dotNumber"
              name="dotNumber"
              value={formData.dotNumber}
              onChange={handleInputChange}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.dotNumber ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter DOT number"
            />
            {errors.dotNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.dotNumber}</p>
            )}
          </div>

          <div>
            <label htmlFor="mcNumber" className="block text-sm font-medium text-gray-700">
              MC Number
            </label>
            <input
              type="text"
              id="mcNumber"
              name="mcNumber"
              value={formData.mcNumber}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="MC-123456 (optional)"
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <MapPin className="h-5 w-5 mr-2 text-blue-600" />
          Business Address
        </h3>

        <div>
          <label htmlFor="address.street" className="block text-sm font-medium text-gray-700">
            Street Address *
          </label>
          <input
            type="text"
            id="address.street"
            name="address.street"
            value={formData.address.street}
            onChange={handleInputChange}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors['address.street'] ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter street address"
          />
          {errors['address.street'] && (
            <p className="mt-1 text-sm text-red-600">{errors['address.street']}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="address.city" className="block text-sm font-medium text-gray-700">
              City *
            </label>
            <input
              type="text"
              id="address.city"
              name="address.city"
              value={formData.address.city}
              onChange={handleInputChange}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors['address.city'] ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="City"
            />
            {errors['address.city'] && (
              <p className="mt-1 text-sm text-red-600">{errors['address.city']}</p>
            )}
          </div>

          <div>
            <label htmlFor="address.state" className="block text-sm font-medium text-gray-700">
              State *
            </label>
            <select
              id="address.state"
              name="address.state"
              value={formData.address.state}
              onChange={handleInputChange}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors['address.state'] ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select State</option>
              {STATES.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            {errors['address.state'] && (
              <p className="mt-1 text-sm text-red-600">{errors['address.state']}</p>
            )}
          </div>

          <div>
            <label htmlFor="address.zipCode" className="block text-sm font-medium text-gray-700">
              ZIP Code *
            </label>
            <input
              type="text"
              id="address.zipCode"
              name="address.zipCode"
              value={formData.address.zipCode}
              onChange={handleZipChange}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors['address.zipCode'] ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="12345 or 12345-6789"
              maxLength="10"
            />
            {errors['address.zipCode'] && (
              <p className="mt-1 text-sm text-red-600">{errors['address.zipCode']}</p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <Phone className="h-5 w-5 mr-2 text-blue-600" />
          Contact Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <div className="mt-1 relative">
              <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.phone ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="(555) 123-4567"
                maxLength="14"
              />
            </div>
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <div className="mt-1 relative">
              <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="company@example.com"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="website" className="block text-sm font-medium text-gray-700">
            Website (Optional)
          </label>
          <div className="mt-1 relative">
            <Globe className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://www.company.com"
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">
          Subscription Plan
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {SUBSCRIPTION_PLANS.map(plan => (
            <label key={plan.value} className="relative">
              <input
                type="radio"
                name="subscriptionPlan"
                value={plan.value}
                checked={formData.subscriptionPlan === plan.value}
                onChange={handleInputChange}
                className="sr-only"
              />
              <div className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                formData.subscriptionPlan === plan.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <div className="font-medium text-gray-900">{plan.label}</div>
                <div className="text-sm text-gray-500 mt-1">{plan.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {isSaving && (
        <div className="flex items-center pt-2 text-sm text-blue-600">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
          Saving company information…
        </div>
      )}

      {lastSavedAt && (
        <p className="text-xs text-gray-500">
          Last saved at {lastSavedAt.toLocaleTimeString()}
        </p>
      )}
    </form>
  );
};

export default CompanySetupStep;
